import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";

const navItems = [
  { name: "Home", href: "/" },
  { name: "About Us", href: "/aboutPage" },
  { name: "Services", href: "/servicespage" },
  { name: "Gallery", href: "/gallery" },
  { name: "Career", href: "/careerpage" },
  { name: "Contact Us", href: "/contactus" },
  {
    name: "Products",
    href: "/products",
    dropdown: true,
    children: [
      { name: "HRMS (Human Resource Management System)", href: "https://hrmsm.com/home" },
      {
        name: "AIFaceTrack (AI-Based Attendance & Security)",
        href: "/aifacetrack",
      },
      { name: "CRM (Customer Relationship Management)", href: "/crm" },
      { name: "QMS (Queue Management System)", href: "/qms" },
      { name: "ERP (Enterprise Resource Planning)", href: "/contact" },
      { name: "POS (Point of Sale System)", href: "/contact" },
      { name: "AI Automation (Marketing Automation Platform)", href: "/contact" },
      { name: "VMS (Vendor Management System)", href: "/contact" },
      { name: "DMS (Dealer Management System)", href: "/contact" },
      { name: "Supply & Logistics (Supply Chain & Logistics Management)", href: "/contact" },
      { name: "Task Allocation (Task & Workforce Management System)", href: "/contact" },
      { name: "LMS (Learning Management System)", href: "/contact" },
    ],
  },
];

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 960 : false
  );
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 960;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileMenuOpen(false);
      } else {
        setDesktopDropdownOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close desktop dropdown on outside click or route change
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDesktopDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setDesktopDropdownOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header className="navbar">
        <div className="navbar-container">
          <div className="logo">
            <Link to="/" className="scrolled-logo" aria-label="ITSoftlab Home">
              <div className="ll" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav
            className={`nav-links ${isMobile ? "hide" : ""}`}
            aria-label="Main Navigation"
          >
            {navItems.map((item) => {
              if (item.dropdown) {
                return (
                  <div
                    className={`dropdown ${desktopDropdownOpen ? "dropdown-open" : ""}`}
                    key={item.name}
                    ref={dropdownRef}
                    onMouseEnter={() => setDesktopDropdownOpen(true)}
                    onMouseLeave={() => setDesktopDropdownOpen(false)}
                  >
                    <button
                      type="button"
                      className="scrolled-link dropdown-toggle"
                      onClick={() => setDesktopDropdownOpen((prev) => !prev)}
                      aria-expanded={desktopDropdownOpen}
                      aria-haspopup="true"
                    >
                      <span>{item.name}</span>
                      <svg
                        className={`dropdown-arrow ${desktopDropdownOpen ? "open" : ""}`}
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div
                      className={`dropdown-menu modern-dropdown ${
                        desktopDropdownOpen ? "is-visible" : ""
                      }`}
                      role="menu"
                    >
                      {item.children.map((child, index) => {
                        const isExternal = child.href.startsWith("http");
                        return isExternal ? (
                          <a
                            key={child.name}
                            href={child.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="dropdown-item modern-dropdown-item"
                            style={{ "--item-index": index }}
                            role="menuitem"
                          >
                            <div className="dropdown-item-content">
                              <div className="dropdown-item-icon">
                                <ChevronRight size={18} />
                              </div>
                              <div className="dropdown-item-text">
                                <span className="dropdown-item-name">
                                  {child.name}
                                </span>
                              </div>
                            </div>
                            <div className="dropdown-item-hover-bg" />
                          </a>
                        ) : (
                          <Link
                            key={child.name}
                            to={child.href}
                            className={`dropdown-item modern-dropdown-item ${
                              location.pathname === child.href ? "active" : ""
                            }`}
                            style={{ "--item-index": index }}
                            role="menuitem"
                          >
                            <div className="dropdown-item-content">
                              <div className="dropdown-item-icon">
                                <ChevronRight size={18} />
                              </div>
                              <div className="dropdown-item-text">
                                <span className="dropdown-item-name">
                                  {child.name}
                                </span>
                              </div>
                            </div>
                            <div className="dropdown-item-hover-bg" />
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              } else {
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`scrolled-link ${
                      location.pathname === item.href ? "active" : ""
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              }
            })}
          </nav>

          <div className="nav-right">
            <Link to="/" className="get-started-link">
              <button className="get-started-btn">Get Started</button>
            </Link>

            {/* Mobile Menu Button */}
            <button
              className="menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close Menu" : "Open Menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        <div
          className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile Navigation Drawer"
        >
          {navItems.map((item) => {
            if (item.dropdown) {
              return (
                <div key={item.name} className="mobile-dropdown">
                  <button
                    className="mobile-dropdown-title"
                    onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                    aria-expanded={mobileDropdownOpen}
                  >
                    <span>{item.name}</span>
                    <svg
                      className={`mobile-dropdown-arrow ${
                        mobileDropdownOpen ? "open" : ""
                      }`}
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  {mobileDropdownOpen && (
                    <div className="mobile-dropdown-menu">
                      {item.children.map((child) => {
                        const isExternal = child.href.startsWith("http");
                        return isExternal ? (
                          <a
                            key={child.name}
                            href={child.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setMobileDropdownOpen(false);
                            }}
                            className="mobile-dropdown-link"
                          >
                            {child.name}
                          </a>
                        ) : (
                          <Link
                            key={child.name}
                            to={child.href}
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setMobileDropdownOpen(false);
                            }}
                            className={`mobile-dropdown-link ${
                              location.pathname === child.href ? "active" : ""
                            }`}
                          >
                            {child.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            } else {
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`mobile-nav-link ${
                    location.pathname === item.href ? "active" : ""
                  }`}
                >
                  {item.name}
                </Link>
              );
            }
          })}

          <div className="mobile-action-wrap">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="mobile-cta-btn"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      <style>
        {`
  /* Navbar Container */
  .scrolled-link {
    font-size: 16px;
    color: rgb(6, 0, 59);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.25s ease;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 6px;
    text-decoration: none;
  }

  .scrolled-link:hover {
    color: #2563eb;
    background: rgba(37, 99, 235, 0.08);
  }

  .scrolled-link.active,
  .dropdown-item.active {
    background-color: rgb(37, 85, 218);
    color: white !important;
    border-radius: 6px;
    padding: 6px 12px;
  }

  .dropdown-toggle {
    outline: none;
  }

  .navbar {
    width: 100%;
    background: rgba(255, 255, 255, 0.88);
    background: linear-gradient(to right, rgba(255, 255, 255, 0.95), rgba(220, 238, 255, 0.85));
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    box-shadow: 0 2px 20px rgba(15, 23, 42, 0.06);
    border-bottom: 1px solid rgba(226, 232, 240, 0.7);
  }

  .navbar-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px max(20px, var(--sar)) 8px max(20px, var(--sal));
    max-width: 1540px;
    margin: auto;
    width: 100%;
    box-sizing: border-box;
  }

  /* Logo */
  .logo {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
  }

  .scrolled-logo {
    display: flex;
    align-items: center;
    text-decoration: none;
  }

  .ll {
    height: 58px;
    width: 180px;
    position: relative;
    background-image: url("/images/log.png");
    background-size: contain;
    background-position: center left;
    background-repeat: no-repeat;
    mix-blend-mode: multiply;
    transition: transform 0.25s ease;
  }

  /* Desktop Navigation */
  .nav-links {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    white-space: nowrap;
  }

  .nav-links.hide {
    display: none !important;
  }

  .nav-right {
    flex: 0 0 auto;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
  }

  .get-started-link {
    text-decoration: none;
  }

  .get-started-btn {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: white;
    padding: 8px 18px;
    border: none;
    border-radius: 25px;
    cursor: pointer;
    font-size: 14.5px;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
    transition: all 0.25s ease;
  }

  .get-started-btn:hover {
    background: linear-gradient(135deg, #1d4ed8, #1e40af);
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(37, 99, 235, 0.35);
  }

  /* Mobile Menu Button (Accessible 44px touch target) */
  .menu-toggle {
    display: none;
    background: none;
    border: none;
    color: rgb(6, 0, 59);
    cursor: pointer;
    padding: 8px;
    min-width: 44px;
    min-height: 44px;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: background 0.2s ease;
  }

  .menu-toggle:hover {
    background: rgba(37, 99, 235, 0.1);
  }

  /* Mobile Navigation Drawer */
  .mobile-menu {
    display: none;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: 100%;
    background: #ffffff;
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.15);
    padding: 12px 0 24px;
    max-height: calc(100vh - 65px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    border-bottom: 1px solid rgba(226, 232, 240, 0.9);
  }

  .mobile-menu.open {
    display: flex;
    animation: drawerSlideDown 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  @keyframes drawerSlideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .mobile-nav-link {
    padding: 13px 24px;
    min-height: 46px;
    text-decoration: none;
    color: #1e293b;
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    transition: all 0.2s ease;
    border-left: 4px solid transparent;
  }

  .mobile-nav-link:hover,
  .mobile-nav-link:focus {
    background: #f1f5f9;
    border-left-color: #2563eb;
    color: #2563eb;
  }

  .mobile-nav-link.active {
    background: rgba(37, 99, 235, 0.08);
    color: #2563eb;
    border-left-color: #2563eb;
  }

  .mobile-action-wrap {
    padding: 16px 24px 8px;
  }

  .mobile-cta-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 44px;
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: white;
    text-decoration: none;
    font-weight: 700;
    border-radius: 12px;
    font-size: 15px;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.3);
  }

  /* ================= MODERN DROPDOWN STYLES ================= */
  .dropdown {
    position: relative;
    display: inline-block;
  }

  .dropdown-arrow {
    transition: transform 0.25s ease;
    color: rgb(6, 0, 59);
  }

  .dropdown-arrow.open,
  .dropdown:hover .dropdown-arrow {
    transform: rotate(180deg);
  }

  .modern-dropdown {
    position: absolute;
    background: #ffffff;
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(226, 232, 240, 0.85);
    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.16), 0 4px 12px rgba(15, 23, 42, 0.05);
    border-radius: 16px;
    padding: 12px;
    top: calc(100% + 8px);
    right: -20px;
    display: none;
    width: min(480px, calc(100vw - 24px));
    max-height: 440px;
    overflow-y: auto;
    z-index: 1050;
    scrollbar-width: thin;
    scrollbar-color: rgba(59, 130, 246, 0.4) transparent;
  }

  .modern-dropdown.is-visible,
  .dropdown:hover .modern-dropdown {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
    animation: dropdownAppear 0.25s ease forwards;
  }

  @keyframes dropdownAppear {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modern-dropdown-item {
    position: relative;
    padding: 10px 14px;
    min-height: 44px;
    display: flex;
    align-items: center;
    text-decoration: none;
    border-radius: 10px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    font-size: 13.5px;
    font-weight: 500;
  }

  .modern-dropdown-item:hover {
    background-color: #f0f7ff;
    transform: translateX(3px);
  }

  .dropdown-item-content {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    position: relative;
    z-index: 2;
  }

  .dropdown-item-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: rgba(59, 130, 246, 0.12);
    border-radius: 6px;
    color: #2563eb;
    flex-shrink: 0;
  }

  .dropdown-item-name {
    color: #1e293b;
    line-height: 1.35;
    font-weight: 600;
  }

  /* ================= MOBILE DROPDOWN STYLES ================= */
  .mobile-dropdown {
    margin: 0;
    border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  }

  .mobile-dropdown-title {
    width: 100%;
    min-height: 48px;
    padding: 13px 24px;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8fafc;
    border: none;
    cursor: pointer;
    color: #2563eb;
    font-size: 16px;
    transition: background 0.2s ease;
  }

  .mobile-dropdown-arrow {
    transition: transform 0.25s ease;
    color: #2563eb;
  }

  .mobile-dropdown-arrow.open {
    transform: rotate(180deg);
  }

  .mobile-dropdown-menu {
    background: #f1f5f9;
    padding: 6px 0;
    max-height: 320px;
    overflow-y: auto;
  }

  .mobile-dropdown-link {
    padding: 10px 24px 10px 36px;
    min-height: 42px;
    display: flex;
    align-items: center;
    color: #334155;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: all 0.2s ease;
    border-left: 3px solid transparent;
  }

  .mobile-dropdown-link:hover {
    background: rgba(37, 99, 235, 0.1);
    color: #2563eb;
    border-left-color: #2563eb;
  }

  .mobile-dropdown-link.active {
    background: #2563eb;
    color: white !important;
    border-left-color: white;
  }

  /* ================= RESPONSIVE MEDIA QUERIES ================= */
  @media (max-width: 1200px) {
    .nav-links {
      gap: 12px;
    }
    .scrolled-link {
      font-size: 15px;
      padding: 5px 8px;
    }
    .ll {
      height: 52px;
      width: 160px;
    }
  }

  @media (max-width: 960px) {
    .nav-links {
      display: none !important;
    }
    .menu-toggle {
      display: flex;
    }
    .get-started-btn {
      padding: 7px 14px;
      font-size: 13.5px;
    }
  }

  @media (max-width: 600px) {
    .ll {
      height: 44px;
      width: 135px;
    }
    .get-started-link {
      display: none;
    }
  }

  @media (max-width: 360px) {
    .ll {
      height: 38px;
      width: 115px;
    }
    .navbar-container {
      padding: 6px 12px;
    }
  }
        `}
      </style>
    </>
  );
}
